import React, { useState, useEffect } from 'react';
import {
  Typography, Card, CardContent, TextField, Button, Alert, CircularProgress, Grid, Divider,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { ApiService } from '../../services/api';

const SEARCH_PROVIDERS = [
  { id: 'gemini', label: 'Google Gemini (Search grounding)' },
  { id: 'you', label: 'You.com (Web Search API)' },
];

const SEARCH_MODELS: Record<string, { id: string; label: string }[]> = {
  gemini: [
    { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash (current Surf)' },
    { id: 'gemini-flash-latest', label: 'Gemini Flash latest' },
    { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite' },
  ],
  you: [
    { id: 'ydc-search-v1', label: "You.com Web Search (no model choice - fixed endpoint)" },
  ],
};

const ANALYSIS_PROVIDERS = [
  { id: 'gemini', label: 'Google Gemini' },
  { id: 'zai', label: 'Z.ai (GLM)' },
  { id: 'claude', label: 'Anthropic Claude' },
];

const ANALYSIS_MODELS: Record<string, { id: string; label: string }[]> = {
  gemini: [
    { id: 'gemini-2.5-flash-lite', label: '2.5 Flash-Lite (cheapest)' },
    { id: 'gemini-3.5-flash-lite', label: '3.5 Flash-Lite' },
    { id: 'gemini-2.5-flash', label: '2.5 Flash' },
    { id: 'gemini-3.5-flash', label: '3.5 Flash' },
  ],
  zai: [
    { id: 'glm-4.5-flash', label: 'GLM-4.5 Flash (cheap)' },
    { id: 'glm-4.7-flash', label: 'GLM-4.7 Flash' },
    { id: 'glm-4.6', label: 'GLM-4.6' },
    { id: 'glm-5-turbo', label: 'GLM-5 Turbo' },
    { id: 'glm-5.3', label: 'GLM-5.3' },
  ],
  claude: [
    { id: 'claude-haiku-5-5', label: 'Haiku 5.5 (cheapest)' },
    { id: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
    { id: 'claude-opus-5-5', label: 'Opus 5.5' },
  ],
};

const SearchLlmSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [llmSettings, setLlmSettings] = useState({
    search_provider: 'gemini',
    search_model: 'gemini-3.5-flash',
    analysis_provider: 'gemini',
    analysis_model: 'gemini-2.5-flash-lite',
    system_prompt: '',
    query_prompt: '',
    ai_rules: ''
  });

  const [knowledgeBase, setKnowledgeBase] = useState('');
  const [newslettersStr, setNewslettersStr] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const [llm, kb, nl] = await Promise.all([
        ApiService.getLlmSettings(),
        ApiService.getKnowledgeBase(),
        ApiService.getNewsletterPresets()
      ]);
      if (llm) setLlmSettings({
        search_provider: llm.search_provider || 'gemini',
        search_model: llm.search_model || 'gemini-3.5-flash',
        analysis_provider: llm.analysis_provider || 'gemini',
        analysis_model: llm.analysis_model || 'gemini-2.5-flash-lite',
        system_prompt: llm.system_prompt || '',
        query_prompt: llm.query_prompt || '',
        ai_rules: llm.ai_rules || ''
      });
      if (kb) setKnowledgeBase(kb.content || '');
      if (nl) setNewslettersStr(JSON.stringify(nl, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const handleSaveLlm = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.updateLlmSettings(llmSettings);
      setSuccess('LLM Settings saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save LLM settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKb = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.updateKnowledgeBase(knowledgeBase);
      setSuccess('Knowledge Base saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Knowledge Base');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewsletters = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const parsed = JSON.parse(newslettersStr);
      await ApiService.updateNewsletterPresets(parsed);
      setSuccess('Newsletter presets saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Newsletter presets. Ensure it is valid JSON.');
    } finally {
      setLoading(false);
    }
  };

  const searchModels = SEARCH_MODELS[llmSettings.search_provider] || SEARCH_MODELS.gemini;
  const analysisModels = ANALYSIS_MODELS[llmSettings.analysis_provider] || ANALYSIS_MODELS.gemini;

  if (loading && !loaded) {
    return <CircularProgress />;
  }

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%', overflowY: 'auto' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Search & LLM Settings</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          API keys live on <strong>Settings → APIs</strong>. This page only picks which model to use.
        </Alert>

        <Typography variant="h6" sx={{ mb: 1 }}>Search</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Used by Surf to look up company websites on the web. Gemini also suggests a category tag per result;
          You.com returns plain search results with no tag suggestion.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Search provider</InputLabel>
              <Select
                label="Search provider"
                value={llmSettings.search_provider}
                onChange={e => {
                  const search_provider = String(e.target.value);
                  const models = SEARCH_MODELS[search_provider] || [];
                  setLlmSettings({
                    ...llmSettings,
                    search_provider,
                    search_model: models[0]?.id || llmSettings.search_model,
                  });
                }}
              >
                {SEARCH_PROVIDERS.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Search model</InputLabel>
              <Select
                label="Search model"
                value={llmSettings.search_model}
                onChange={e => setLlmSettings({ ...llmSettings, search_model: String(e.target.value) })}
              >
                {searchModels.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Analysis</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cheap model that reads titles/snippets (and later page text) to pick an official website.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Analysis provider</InputLabel>
              <Select
                label="Analysis provider"
                value={llmSettings.analysis_provider}
                onChange={e => {
                  const analysis_provider = String(e.target.value);
                  const models = ANALYSIS_MODELS[analysis_provider] || [];
                  setLlmSettings({
                    ...llmSettings,
                    analysis_provider,
                    analysis_model: models[0]?.id || llmSettings.analysis_model,
                  });
                }}
              >
                {ANALYSIS_PROVIDERS.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Analysis model</InputLabel>
              <Select
                label="Analysis model"
                value={llmSettings.analysis_model}
                onChange={e => setLlmSettings({ ...llmSettings, analysis_model: String(e.target.value) })}
              >
                {analysisModels.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Prompts & General AI Rules</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="System Prompt" multiline rows={3} size="small"
              value={llmSettings.system_prompt}
              onChange={e => setLlmSettings({ ...llmSettings, system_prompt: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Query Prompt" multiline rows={2} size="small"
              value={llmSettings.query_prompt}
              onChange={e => setLlmSettings({ ...llmSettings, query_prompt: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="General AI Search & Domain Analysis Rules"
              placeholder="Tell the AI general guidelines to keep in mind (e.g. 'always check for cannabis associations', 'ignore CBD affiliate sites', etc.)"
              multiline
              rows={4}
              size="small"
              value={llmSettings.ai_rules}
              onChange={e => setLlmSettings({ ...llmSettings, ai_rules: e.target.value })}
              helperText="Used when analyzing website titles, descriptions, and tag matching."
            />
          </Grid>
        </Grid>
        <Button variant="contained" onClick={handleSaveLlm} sx={{ mt: 2, backgroundColor: '#2563eb', '&:hover': { backgroundColor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600 }} disabled={loading}>Save LLM Settings & Rules</Button>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>Tagging Knowledge Base</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste the document contents to be used for LLM context when tagging companies.
        </Typography>
        <TextField
          fullWidth multiline rows={10} size="small"
          value={knowledgeBase}
          onChange={e => setKnowledgeBase(e.target.value)}
        />
        <Button variant="contained" onClick={handleSaveKb} sx={{ mt: 2 }} disabled={loading}>Save Knowledge Base</Button>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>Newsletter Presets</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define presets as a JSON array. Example: <br/>
          <code>[ {`{ "tag_id": 1, "newsletter_ids": [1, 2] }`} ]</code>
        </Typography>
        <TextField
          fullWidth multiline rows={6} size="small"
          value={newslettersStr}
          onChange={e => setNewslettersStr(e.target.value)}
        />
        <Button variant="contained" onClick={handleSaveNewsletters} sx={{ mt: 2 }} disabled={loading}>Save Newsletter Presets</Button>

      </CardContent>
    </Card>
  );
};

export default SearchLlmSettings;
